import Link from "next/link";
import {
  ArrowRight,
  BriefcaseBusiness,
  HeartHandshake,
  Lightbulb,
  Mail,
  Users,
} from "lucide-react";

import Footer from "@/pagecomponents/newcomponents/Footer";
import Navbar from "@/pagecomponents/newcomponents/Navbar";
import SEOTags from "@/components/SEOTags";

const principles = [
  {
    icon: Users,
    title: "Start with the job seeker",
    description:
      "We build around the real work, uncertainty, and time pressure people experience during a job search.",
  },
  {
    icon: Lightbulb,
    title: "Make complexity feel simple",
    description:
      "We turn complicated application workflows into clear, useful product experiences.",
  },
  {
    icon: HeartHandshake,
    title: "Work with care",
    description:
      "We value thoughtful decisions, direct communication, and teammates who take ownership of the outcome.",
  },
];

export default function Careers() {
  return (
    <>
      <SEOTags
        title="Careers at LifeShack"
        description="Explore careers at LifeShack and share your resume for future opportunities building AI-powered tools for job seekers."
        url="https://www.lifeshack.com/careers/"
      />

      <Navbar secondary />

      <main className="bg-white text-slate-950">
        <section className="relative isolate overflow-hidden border-b border-slate-200 bg-gradient-to-b from-indigo-50 via-white to-white">
          <div className="pointer-events-none absolute left-1/2 top-[-9rem] -z-10 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-fuchsia-200/40 blur-3xl sm:h-[36rem] sm:w-[36rem]" />
          <div className="pointer-events-none absolute left-[8%] top-24 -z-10 h-32 w-32 rounded-full bg-indigo-200/40 blur-3xl" />
          <div className="pointer-events-none absolute right-[8%] top-40 -z-10 h-40 w-40 rounded-full bg-sky-200/40 blur-3xl" />

          <div className="mx-auto max-w-5xl px-6 py-20 text-center sm:py-28 lg:px-8 lg:py-32">
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm backdrop-blur">
              <span className="h-2 w-2 rounded-full bg-indigo-500" />
              Careers at LifeShack
            </div>

            <h1 className="mx-auto mt-6 max-w-4xl text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              Help us build a better way to find work.
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-slate-600 sm:text-xl sm:leading-9">
              LifeShack is building tools that reduce repetitive application
              work and help job seekers spend more time on the human parts of
              their search.
            </p>

            <a
              href="#openings"
              className="mt-8 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-gray-900 px-5 py-3 font-semibold text-white shadow-lg shadow-slate-900/15 transition hover:-translate-y-0.5 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              View current openings
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
          </div>
        </section>

        <section id="openings" className="scroll-mt-24 px-6 py-16 sm:py-24 lg:px-8">
          <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-slate-800 bg-gray-900 px-6 py-10 text-white shadow-2xl shadow-slate-300/50 sm:px-10 sm:py-12 lg:px-12">
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-indigo-500/20 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 left-1/3 h-56 w-56 rounded-full bg-fuchsia-500/10 blur-3xl" />

            <div className="relative grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="max-w-2xl">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-indigo-200">
                    <BriefcaseBusiness className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <p className="text-sm font-semibold uppercase tracking-[0.16em] text-indigo-300">
                    Current openings
                  </p>
                </div>
                <h2 className="mt-6 text-3xl font-semibold tracking-tight sm:text-4xl">
                  No open roles right now
                </h2>
                <p className="mt-4 text-lg leading-8 text-slate-300">
                  We do not have any open roles right now, but you can still
                  send us your resume. We are always interested in meeting
                  thoughtful people who care about improving the job search.
                </p>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-5 backdrop-blur-sm sm:p-6 lg:w-72">
                <p className="text-sm font-medium text-slate-300">
                  Interested in future opportunities?
                </p>
                <a
                  href="mailto:careers@lifeshack.com?subject=Future%20opportunities%20at%20LifeShack"
                  className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 font-semibold text-slate-950 shadow-sm transition hover:-translate-y-0.5 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2 focus:ring-offset-gray-900"
                >
                  Send your resume
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
                <a
                  className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-300 transition hover:text-white"
                  href="mailto:careers@lifeshack.com"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  careers@lifeshack.com
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-slate-50 px-6 py-16 sm:py-24 lg:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <p className="text-sm font-semibold uppercase tracking-[0.16em] text-indigo-700">
                How we work
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                The kind of team we&apos;re building
              </h2>
              <p className="mt-4 text-lg leading-8 text-slate-600">
                We care about useful products, clear thinking, and people who
                want their work to make a practical difference.
              </p>
            </div>

            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {principles.map(({ icon: Icon, title, description }, index) => (
                <article
                  key={title}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-indigo-200 hover:shadow-xl hover:shadow-indigo-100/60"
                >
                  <span className="absolute right-6 top-5 text-sm font-semibold tracking-widest text-slate-300 transition group-hover:text-indigo-300">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold text-slate-950">
                    {title}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-600">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="px-6 py-16 sm:py-24 lg:px-8">
          <div className="relative mx-auto max-w-5xl overflow-hidden rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 px-6 py-10 sm:px-10 sm:py-12">
            <div className="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full border-[32px] border-white/70" />
            <div className="relative flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between">
              <div className="max-w-2xl">
                <p className="text-sm font-semibold uppercase tracking-[0.16em] text-indigo-700">
                  More about us
                </p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
                  Get to know LifeShack
                </h2>
                <p className="mt-4 text-lg leading-8 text-slate-600">
                  Learn more about the company, our mission, and the job-search
                  problems we are working to solve.
                </p>
              </div>
              <Link href="/about/">
                <a className="inline-flex min-h-[48px] shrink-0 items-center justify-center gap-2 self-start rounded-xl border border-indigo-200 bg-white px-5 py-3 font-semibold text-indigo-700 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:text-indigo-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:self-auto">
                  About LifeShack
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}
